import { AcquisitionFlow, getAcquisitionFlow } from './acquisition-utils';
import { encrypt, decrypt, generateIV, generateKey } from './cob-utils';

interface IScopeMap { 
    [key: string]: string | string[] 
}  

const getScope = (flow: AcquisitionFlow): string => {
    const scope = {
        [AcquisitionFlow.PQ]: '/content/cob-corp-acquisition/us/en/pre-qualification/*',
        [AcquisitionFlow.PA]: '/content/cob-corp-acquisition/us/en/pre-approved/*',
        [AcquisitionFlow.SkipPQ]: '/content/cob-corp-acquisition/us/en/full-application/*',
        [AcquisitionFlow.PqOfOne]: '/content/cob-corp-acquisition/us/en/pq-one/*',
        [AcquisitionFlow.PHA]: '/content/cob-corp-acquisition/us/en/pha-secured/*',
    };
    return scope[flow];
};

const SessionStorage = {
    contains(propertyName: string): boolean {
        return sessionStorage.getItem(propertyName) !== null;
    },

    get(itemKey: string): string {
        return sessionStorage.getItem(itemKey);
    },

    // Removing the default or using unknown causes compilation errors across our codebase.
    // Any is used for retro-compatibility
    getObject<TReturnType = any>(itemKey: string): TReturnType {
        return JSON.parse(sessionStorage.getItem(itemKey)!) as TReturnType;
    },

    // Removing the default or using unknown causes compilation errors across our codebase.
    // Any is used for retro-compatibility
    tryGet<TReturnType = any>(itemKey: string, defaultReturn: any): TReturnType {
        if (this.verifyNotEmpty(itemKey)) {
            return this.get(itemKey) as TReturnType;
        }
        
        return defaultReturn;
    },

    // Removing the default or using unknown causes compilation errors across our codebase.
    // Any is used for retro-compatibility
    tryGetObject<TReturnType = any>(itemKey: string, defaultReturn: any): TReturnType {
        if (this.verifyNotEmpty(itemKey)) {
            try {
                return this.getObject<TReturnType>(itemKey);
            } catch (error) {
                console.error('SessionStorage can not read improper JSON');
                return defaultReturn;
            }
        }
        
        return defaultReturn;
    },

    // Removing the default or using unknown causes compilation errors across our codebase.
    // Any is used for retro-compatibility
    async tryGetCipher<TReturnType = any>(itemKey: string, defaultReturn: any): Promise<TReturnType> {
        if (this.verifyNotEmpty(itemKey)) {
            try {
                return await this.getCipher(itemKey, !(typeof defaultReturn === 'string'));
            } catch (error) {
                console.error('Error trying to get item, returning default', error);
                return defaultReturn;
            }
        }
        return defaultReturn;
    },

    // Removing the default or using unknown causes compilation errors across our codebase.
    // Any is used for retro-compatibility
    async tryGetObjectOrCipher<TReturnType = any>(itemKey: string, defaultReturn: any): Promise<TReturnType> {
        if (this.verifyNotEmpty(itemKey)) {
            try {
                return this.getObject<TReturnType>(itemKey);
            } catch (error) {
                return await this.tryGetCipher<TReturnType>(itemKey, defaultReturn);
            }
        }
        return defaultReturn;
    },

    remove(itemKey: string): void {
        sessionStorage.removeItem(itemKey);
    },

    removeItems(itemKeys: string[]): void {
        itemKeys.forEach((key) => sessionStorage.removeItem(key));
    },

    set(itemKey: string, itemValue: string): void {
        sessionStorage.setItem(itemKey, itemValue);
    },

    setObject(itemKey: string, itemValue: unknown): void {
        sessionStorage.setItem(itemKey, JSON.stringify(itemValue));
    },

    async setCipher(itemKey: string, itemValue:  string | Record<string, unknown> | unknown = {}): Promise<void> {
        const stringValue = typeof itemValue === 'string' ? itemValue : JSON.stringify(itemValue);
        const iv = generateIV();
        const key = await generateKey(process.env.REACT_APP_SESSION_STORAGE_KEY, iv as unknown as ArrayBuffer);
        const encrypted = await encrypt(stringValue, key, iv as unknown as ArrayBuffer);
        const encryptedArr = Array.from(new Uint8Array(encrypted as ArrayBuffer));
        const encryptedStr = encryptedArr.map(byte => String.fromCharCode(byte)).join('');
        const ivStr = Array.from(iv).map(ch => String.fromCharCode(ch)).join('');
        sessionStorage.setItem(itemKey, btoa(ivStr + encryptedStr));
    },

    // Removing the default or using unknown causes compilation errors across our codebase.
    // Any is used for retro-compatibility
    async getCipher<TReturnType = any>(itemKey: string, isObject?: boolean): Promise<TReturnType> {
        const item = sessionStorage.getItem(itemKey);
        const ivStr = atob(item).slice(0, 12);
        const iv = new Uint8Array(Array.from(ivStr).map(ch => ch.charCodeAt(0)));
        const key = await generateKey(process.env.REACT_APP_SESSION_STORAGE_KEY, iv as unknown as ArrayBuffer);
        const encrypted = atob(item).slice(12);
        const encryptedArr = new Uint8Array(Array.from(encrypted).map(ch => ch.charCodeAt(0)));
        const decrypted = await decrypt(encryptedArr as unknown as ArrayBuffer, key, iv as unknown as ArrayBuffer);
        return (isObject ? JSON.parse(decrypted) : decrypted) as TReturnType;
    },

    /**
     * Helper function for setting the scope as a session storage item.
     * @param itemKey - key for this item
     * @param scope - Path(s) used to determine item scope. Always set as object.
     */
    setScopeMap(itemKey: string, scope: string | string[]): void {
        if (!this.verify('scope_map')) {
            const obj: IScopeMap = {};
            obj[itemKey] = scope;
            this.setObject('scope_map', obj);
        } else {
            const scopeMapObj = this.getObject('scope_map');
            scopeMapObj[itemKey] = scope;
            this.setObject('scope_map', scopeMapObj);
        }
    },

    /*
     * Sets a session storage item with the current flow as the scope. Global listener will check any session storage
     * items with scope to determine if they are inside or outside of their scope. If an item is
     * about to be outside of its scope it will be removed.
     * @param itemKey - key for this item
     * @param itemValue - value or contents for this item
     */
    setObjectWithCurrentScope(itemKey: string, itemValue: unknown): void {
        
        this.setObject(itemKey, itemValue);
        this.setScopeMap(itemKey, getScope(getAcquisitionFlow()));
    },

    async setCipherWithCurrentScope(itemKey: string, itemValue: unknown): Promise<void>  {
        await this.setCipher(itemKey, itemValue);
        this.setScopeMap(itemKey, getScope(getAcquisitionFlow()));
    },

    /**
     * Checks session storage for 'scope_map'.
     * If it exists it will verify that all items in the map are within the valid scope.
     * Anything outside of the valid scope will be removed.
     * if scope_map is empty after removal then it too will be removed.
     */
    removeOutOfScopeItems(): void {
        if (SessionStorage.verify('scope_map')) {
            const scopeMap: IScopeMap = SessionStorage.getObject('scope_map');
            if (!scopeMap) {
                return;
            }
            const currPath = window.location.pathname
                .replace('.html', '')
                .replace('/content/cob-corp-acquisition/us/en', '');
            const isAuthor = window.location.port === '4502';

            for (const [key, value] of Object.entries(scopeMap)) {
                let remove = true;
                const data = (typeof value === 'string') ? [ value ] : value;

                for (let path of data) {
                    if (!isAuthor) {
                        path = path.replace('/content/cob-corp-acquisition/us/en', '');
                    }

                    if (path.includes('*') && !path.match(/(^\*\/[^*]+)|([^*]+\/\*$)|(^\*$)/)) {
                        break;
                    }

                    const isGlobal = path === '*';
                    const isIncluded = currPath.includes(path.replace('*', ''));
                    const isExactPath = path === currPath;

                    if (isGlobal || isIncluded || isExactPath) {
                        remove = false;
                        break;
                    }
                }

                if (remove) {
                    SessionStorage.remove(key);
                    delete scopeMap[`${key}`];

                    if (Object.keys(scopeMap).length === 0) {
                        SessionStorage.remove('scope_map');
                    } else {
                        SessionStorage.setObject('scope_map', scopeMap);
                    }
                }
            }
        } 
    },

    verify(itemKey: string): boolean {
        return this.contains(itemKey) && this.get(itemKey) !== null && this.get(itemKey) !== undefined;
    },

    verifyNotEmpty(itemKey: string): boolean {
        return this.verify(itemKey) && this.get(itemKey) !== '';
    },

    clear(): void {
        return sessionStorage.clear();
    },

    result(): void {
        return sessionStorage.result();
    },

};

export const clearAquisitionData = (): void => {
    SessionStorage.removeItems([
        'personalInfo',
        'cardInfo',
        'prequalResponse',
        'prequalFullAppData',
        'preApprovalFullAppData',
        'pre-approval-data',
        'app-verification',
        'app-approved-data',
        'initResponse',
        'ApplicationId',
        'selectedProgramCode',
        'selectedCardImgPath',
        'fullAppResponse',
        'levelThreeResponse',
        'lastVisitedPage',
        'queryParams',
        'previousVisitedPage',
        'deIndexType',
        'creditLine',
        'dataManagerProgramCode',
        'offerContent',
        'isMobileOnlyApplication',
        'sessionId',
        'appAuthorizedResponse',
        'frontIdImageData',
        'backIdImageData',
        'barcodeData',
        'passportImageData',
        'selfieImageData',
        'isSkipPreQual',
        'isSkipPreCounterOffer',
        'flowData',
        'token',
        'protocol',
        'temp2',
        'acctId',
        'idVerified',
        'PersonalInfoBrowserChannel',
        'bvrslt',
    ]);
};

export default SessionStorage;